import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { woundAPI } from '@/lib/api';
import BodyMap from '@/components/BodyMap';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';

const WUNDARTEN = [
  'Dekubitus',
  'Ulcus cruris arteriosum',
  'Diabetisches Fußsyndrom',
  'OP-Wunde',
  'Sonstige',
];

const KLASSIFIKATIONEN = {
  'Dekubitus': [
    'EPUAP Kategorie I',
    'EPUAP Kategorie II',
    'EPUAP Kategorie III',
    'EPUAP Kategorie IV',
    'EPUAP Tiefe unbekannt',
    'EPUAP Uneinstufbar',
  ],
  'Ulcus cruris arteriosum': [
    'Fontaine Stadium I',
    'Fontaine Stadium IIa',
    'Fontaine Stadium IIb',
    'Fontaine Stadium III',
    'Fontaine Stadium IV',
  ],
  'Diabetisches Fußsyndrom': [
    'Wagner-Armstrong Grad 0A', 'Wagner-Armstrong Grad 0B', 'Wagner-Armstrong Grad 0C', 'Wagner-Armstrong Grad 0D',
    'Wagner-Armstrong Grad 1A', 'Wagner-Armstrong Grad 1B', 'Wagner-Armstrong Grad 1C', 'Wagner-Armstrong Grad 1D',
    'Wagner-Armstrong Grad 2A', 'Wagner-Armstrong Grad 2B', 'Wagner-Armstrong Grad 2C', 'Wagner-Armstrong Grad 2D',
    'Wagner-Armstrong Grad 3A', 'Wagner-Armstrong Grad 3B', 'Wagner-Armstrong Grad 3C', 'Wagner-Armstrong Grad 3D',
    'Wagner-Armstrong Grad 4A', 'Wagner-Armstrong Grad 4B', 'Wagner-Armstrong Grad 4C', 'Wagner-Armstrong Grad 4D',
    'Wagner-Armstrong Grad 5A', 'Wagner-Armstrong Grad 5B', 'Wagner-Armstrong Grad 5C', 'Wagner-Armstrong Grad 5D',
  ],
  'Sonstige': [
    'CEAP C0', 'CEAP C1', 'CEAP C2', 'CEAP C3',
    'CEAP C4a', 'CEAP C4b', 'CEAP C4c',
    'CEAP C5', 'CEAP C6', 'CEAP C6r',
  ],
};

export default function NewWoundPage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    patient_id: patientId,
    wundart: '',
    wundklassifikation: '',
    lokalisation: '',
    lokalisation_detail: '',
    body_region: '',
    status: 'aktiv'
  });

  const klassifikationOptions = KLASSIFIKATIONEN[form.wundart] || [];

  const handleWundartChange = (v) => {
    setForm(prev => ({ ...prev, wundart: v, wundklassifikation: '' }));
  };

  const handleBodySelect = (regionId, regionLabel) => {
    setForm(prev => ({ ...prev, body_region: regionId, lokalisation: regionLabel }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.wundart || !form.lokalisation) {
      toast.error('Wundart und Lokalisation sind Pflichtfelder');
      return;
    }
    setLoading(true);
    try {
      const res = await woundAPI.create(form);
      toast.success('Wunde erfolgreich angelegt');
      navigate(`/wunde/${res.data.id}/neuer-bericht`);
    } catch (err) {
      toast.error('Fehler beim Anlegen der Wunde');
    } finally { setLoading(false); }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-2xl mx-auto space-y-4" data-testid="new-wound-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate(-1)} className="p-2 rounded-xl" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-xl md:text-2xl font-heading font-bold text-slate-900">Neue Wunde</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base">Wundart</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select value={form.wundart} onValueChange={handleWundartChange}>
              <SelectTrigger className="h-12" data-testid="wundart-select">
                <SelectValue placeholder="Wundart auswählen..." />
              </SelectTrigger>
              <SelectContent>
                {WUNDARTEN.map(w => <SelectItem key={w} value={w}>{w}</SelectItem>)}
              </SelectContent>
            </Select>

            {klassifikationOptions.length > 0 && (
              <div className="space-y-2 animate-fade-in">
                <Label className="text-sm font-medium text-slate-700">Wundklassifikation</Label>
                <Select value={form.wundklassifikation} onValueChange={v => setForm(prev => ({ ...prev, wundklassifikation: v }))}>
                  <SelectTrigger className="h-12" data-testid="klassifikation-select">
                    <SelectValue placeholder="Klassifikation auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {klassifikationOptions.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base">Lokalisation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-500">Tippen Sie auf die betroffene Körperregion:</p>

            <BodyMap
              selectedRegion={form.body_region}
              onSelect={handleBodySelect}
            />

            <div className="space-y-2">
              <Label>Detaillierte Beschreibung</Label>
              <Input
                data-testid="lokalisation-detail-input"
                value={form.lokalisation_detail}
                onChange={e => setForm(prev => ({ ...prev, lokalisation_detail: e.target.value }))}
                placeholder="z.B. Ferse rechts, lateral"
                className="h-12"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="flex-1 h-12 rounded-xl">
            Abbrechen
          </Button>
          <Button type="submit" disabled={loading} className="flex-1 h-12 bg-teal-700 hover:bg-teal-800 text-white rounded-xl" data-testid="save-wound-btn">
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Speichern...' : 'Wunde anlegen & Bericht starten'}
          </Button>
        </div>
      </form>
    </div>
  );
}
